import type { PublicPluginAPI, OptionalPlugin } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import type { ConfluenceUpdateImageOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { remixRegenerateExperience } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender/remix-regenerate-experience';

import type { PassThroughCommandProps } from '../command-handlers/command-handler';

export class DocumentImageUpdateCommandProcessor {
	private onUploadAndInsertMedia?: PassThroughCommandProps['onUploadAndInsertMedia'];
	private editorAPI?: PublicPluginAPI<[OptionalPlugin<MalleableUiPlugin>]>;
	private hasPrepared = false;
	private lastTraceId?: string;
	private remixSubtype?: string;
	/** Attempt token captured at stream start. */
	private inlineRovoEditAttemptId?: string;

	constructor(
		onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia'],
		editorAPI?: PublicPluginAPI<[OptionalPlugin<MalleableUiPlugin>]>,
	) {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
		this.editorAPI = editorAPI;
	}

	updateCallback(onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia']): void {
		this.onUploadAndInsertMedia = onUploadAndInsertMedia;
	}

	streamChunk(
		data: ConfluenceUpdateImageOnPageData,
		isFinished: boolean,
		traceId?: string,
		_singleInstrumentationId?: string,
	): void {
		this.lastTraceId = traceId ?? this.lastTraceId;

		// If the stream errored, clear the AI generating state and bail out
		if (
			'__isError' in data &&
			(data as ConfluenceUpdateImageOnPageData & { __isError?: boolean }).__isError
		) {
			this.claimInlineRovoEditAttempt(data);
			this.streamError('backend_action', traceId);
			return;
		}

		this.captureRemixMetadata(data);

		// A safety / AUP violation is streamed as a finished ACTION_COMPLETE with
		// `isSafetyViolation: true` and no media. This is an expected outcome, not
		// a system failure, so it must not publish a `taskFail` operational metric
		// (which is pure noise). Publish the outcome as a success and skip the
		// (impossible) media insertion.
		if (isFinished && data.isSafetyViolation) {
			this.claimInlineRovoEditAttempt(data);
			this.reportInlineRovoEditAttempt(this.inlineRovoEditAttemptId, 'handoff');
			const remixSubtype = this.remixSubtype;
			this.hasPrepared = false;
			this.lastTraceId = undefined;
			this.remixSubtype = undefined;
			remixRegenerateExperience.success({
				remixType: 'infographic',
				...(remixSubtype && { remixSubtype }),
			});
			return;
		}
		// Variant-picker image updates are handled FE-side (useUpdateRemix ->
		// useOnComplete uploads + inserts and drives the loading state); the BE
		// push here would double both. Rovo-chat updates aren't tracked, so they
		// still flow through.
		if (
			data.previousMediaFileId &&
			this.editorAPI?.malleableUi?.actions.isVariantUpdateInFlight(data.previousMediaFileId) &&
			fg('platform_editor_ai_remix_toolbar')
		) {
			if (isFinished) {
				// Surface success so the experience tracker closes normally.
				remixRegenerateExperience.success({
					remixType: 'infographic',
					...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
				});
				this.lastTraceId = undefined;
				this.remixSubtype = undefined;
			}
			return;
		}

		this.claimInlineRovoEditAttempt(data);
		// ACTION_PREPARE marks a stream that must report an abort/error outcome.
		if (data.previousMediaFileId) {
			this.hasPrepared = true;
		}

		if (isFinished) {
			const attemptId = this.inlineRovoEditAttemptId;
			this.reportInlineRovoEditAttempt(attemptId, 'result-ready');
			this.reportInlineRovoEditAttempt(attemptId, 'direct-insertion-started');
			this.hasPrepared = false;
			if (!this.onUploadAndInsertMedia) {
				this.reportInlineRovoEditAttempt(attemptId, 'insertion-failed');
				return;
			}
			const uploadPromise = attemptId
				? this.onUploadAndInsertMedia(data.mediaUrl, data, undefined, {
						requireInsertionAcknowledgement: true,
					})
				: this.onUploadAndInsertMedia(data.mediaUrl, data);
			void uploadPromise
				.then((result) => {
					if (attemptId && !result) {
						this.reportInlineRovoEditAttempt(attemptId, 'insertion-failed');
						remixRegenerateExperience.fail({
							error: 'upload_and_insert_failed',
							remixType: 'infographic',
							...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
						});
						this.remixSubtype = undefined;
						return;
					}
					this.reportInlineRovoEditAttempt(attemptId, 'inserted');
					const remixSubtype = this.remixSubtype;
					this.remixSubtype = undefined;
					remixRegenerateExperience.success({
						remixType: 'infographic',
						...(remixSubtype && { remixSubtype }),
					});
					this.lastTraceId = undefined;
					// `result.fieldId` is the upload-side upfront id assigned by
					// mediaClient and written onto mediaNode.attrs.id; the BE-side
					// `data.mediaFileId` is a different identifier and won't match
					// the live node when the toolbar looks up the stack entry.
					const liveFieldId = result?.fieldId;
					if (data.previousMediaFileId && liveFieldId && fg('platform_editor_ai_remix_toolbar')) {
						// `dynamicUiSubtype` is intentionally omitted: the reducer inherits
						// it from the matched entry (the backend's echoed value is
						// unreliable for edits that aren't told the current subtype).
						const cmd = this.editorAPI?.malleableUi?.commands.addBackendRemixHistoryStackEntry({
							previousFieldId: data.previousMediaFileId,
							fieldId: liveFieldId,
						});
						if (cmd) {
							this.editorAPI?.core?.actions.execute(cmd);
						}
					}
				})
				.catch((error: unknown) => {
					this.reportInlineRovoEditAttempt(attemptId, 'insertion-failed');
					const remixSubtype = this.remixSubtype;
					const failureTraceId = traceId ?? this.lastTraceId;
					this.lastTraceId = undefined;
					this.remixSubtype = undefined;
					remixRegenerateExperience.fail({
						error: error instanceof Error ? error.message : 'upload_and_insert_failed',
						failureReason: 'upload_and_insert_failed',
						failureStage: 'frontend_apply',
						remixType: 'infographic',
						...(remixSubtype && { remixSubtype }),
						...(failureTraceId && { traceId: failureTraceId }),
					});
				});
		}
	}

	private captureRemixMetadata(data: ConfluenceUpdateImageOnPageData): void {
		if (data.dynamicUiSubtype) {
			this.remixSubtype = data.dynamicUiSubtype;
		}
	}

	/**
	 * Clears editor-side loading state for image updates. This is safe even before
	 * image prepare data arrives, which can happen when Rovo chat blocks a request.
	 */
	clearStreamingState(
		terminal: 'generation-aborted' | 'generation-error' = 'generation-aborted',
		terminalAttemptId?: string,
	): boolean {
		const attemptId = terminalAttemptId ?? this.inlineRovoEditAttemptId;
		const wasReported = this.reportInlineRovoEditAttempt(attemptId, terminal);
		// Tokenless callers retain the legacy cleanup path.
		if (terminalAttemptId && !wasReported) {
			return false;
		}
		const editorAPI = this.editorAPI as PassThroughCommandProps['editorAPI'] | undefined;
		editorAPI?.aiExperience?.actions.endInlineRovoStreaming?.();
		editorAPI?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		this.hasPrepared = false;
		this.lastTraceId = undefined;
		this.remixSubtype = undefined;
		return true;
	}

	/**
	 * Called when the backend sends an SSE ACTION_ERROR or a prepared image stream is interrupted
	 * to reset the in-flight stream state and fail the remix experience.
	 */
	streamError(
		failureStage: 'backend_action' | 'stream_lifecycle' = 'stream_lifecycle',
		traceId?: string,
	): void {
		const failureTraceId = traceId ?? this.lastTraceId;
		this.hasPrepared = false;
		this.reportInlineRovoEditAttempt(this.inlineRovoEditAttemptId, 'generation-error');
		this.lastTraceId = undefined;
		remixRegenerateExperience.fail({
			error: 'stream_error',
			failureReason:
				failureStage === 'backend_action'
					? 'backend_action_error'
					: 'stream_interrupted_after_prepare',
			failureStage,
			remixType: 'infographic',
			...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
			...(failureTraceId && { traceId: failureTraceId }),
		});
		this.remixSubtype = undefined;
	}

	/**
	 * Called on abort when ACTION_PREPARE was received, matching the legacy image
	 * abort behavior.
	 */
	abortIfPrepared(): void {
		if (this.hasPrepared) {
			this.streamError('stream_lifecycle');
		}
	}

	/**
	 * Called on abort to clear any editor-side image loading state. When ACTION_PREPARE
	 * was received, also fail the remix experience before clearing state.
	 */
	abortAndClearStreamingState(reason?: 'user_abort' | 'aborted'): void {
		if (this.hasPrepared) {
			remixRegenerateExperience.fail({
				error: 'stream_error',
				failureReason: 'stream_interrupted_after_prepare',
				failureStage: 'stream_lifecycle',
				remixType: 'infographic',
				...(this.remixSubtype && { remixSubtype: this.remixSubtype }),
				...(reason && { streamAbortReason: reason }),
				...(this.lastTraceId && { traceId: this.lastTraceId }),
			});
		}
		this.clearStreamingState();
	}

	private claimInlineRovoEditAttempt(data: ConfluenceUpdateImageOnPageData): void {
		if (this.inlineRovoEditAttemptId) {
			return;
		}
		const targetId = data.dynamicUiSource?.startLocalId;
		if (!targetId) {
			return;
		}
		const attemptId = this.editorAPI?.malleableUi?.actions.claimInlineRovoEdit?.(targetId);
		if (attemptId) {
			this.inlineRovoEditAttemptId = attemptId;
		}
	}

	private reportInlineRovoEditAttempt(
		attemptId: string | undefined,
		result:
			| 'generation-aborted'
			| 'generation-error'
			| 'direct-insertion-started'
			| 'handoff'
			| 'insertion-failed'
			| 'inserted'
			| 'result-ready',
	): boolean {
		if (!attemptId) {
			return false;
		}
		const wasReported =
			this.editorAPI?.malleableUi?.actions.reportInlineRovoEdit?.(attemptId, result) ?? false;
		if (
			result === 'generation-aborted' ||
			result === 'generation-error' ||
			result === 'handoff' ||
			result === 'insertion-failed' ||
			result === 'inserted'
		) {
			if (this.inlineRovoEditAttemptId === attemptId) {
				this.inlineRovoEditAttemptId = undefined;
			}
		}
		return wasReported;
	}
}
